'use client'

import { useState } from 'react'
import { Clock } from 'lucide-react'
import type { ProcessedTheme, ProbeRun, ProbeResult } from './processedTypes'
import { watchClasses, compactDate } from './processedTypes'
import { ExpandToggle } from './sharedComponents'
import { Widget } from '../../Widget'

function probeRunOnCount(run: ProbeRun) {
  return run.results.filter((result) => result.isAlive === true).length
}

function probeResultDetail(result: ProbeResult) {
  return result.rawResult?.status ?? result.rawResult?.statusCode ?? result.rawResult?.code ?? result.rawResult?.error
}

function ProbeRunSnapshotCard({
  title,
  run,
  metric,
  theme,
  showJson,
  onToggleJson,
}: {
  title: string
  run: ProbeRun
  metric?: string
  theme: ProcessedTheme
  showJson?: boolean
  onToggleJson?: () => void
}) {
  const c = watchClasses(theme)
  const sortedResults = [...run.results].sort((a, b) => a.testType.localeCompare(b.testType))
  return (
    <div className={`rounded-md border p-4 ${c.line} ${c.soft}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-[0.24em] ${c.label}`}>{title}</div>
          <div className={`mt-2 font-mono text-sm ${c.text}`}>{compactDate(run.createdAt)}</div>
        </div>
        <div className="flex items-center gap-2">
          {metric && <span className="rounded border border-success/40 bg-success-surface px-2 py-1 font-mono text-xs text-success">{metric}</span>}
          {onToggleJson && (
            <button
              onClick={onToggleJson}
              className="font-mono text-[10px] uppercase tracking-widest text-signal hover:text-signal"
            >
              {showJson ? 'Hide' : 'JSON'}
            </button>
          )}
        </div>
      </div>
      <div className={`mt-3 grid gap-2 font-mono text-xs ${c.muted}`}>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {run.probeNode && <span>Node {run.probeNode}</span>}
          {run.probeIp && <span>IP {run.probeIp}</span>}
          {run.triggeredBy && <span>Trigger {run.triggeredBy}</span>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {sortedResults.map((result) => {
            const detail = probeResultDetail(result)
            return (
              <span key={result.id} className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${c.line} bg-card`}>
                <span className={`h-2 w-2 rounded-full ${result.isAlive === true ? 'bg-success' : result.isAlive === false ? 'bg-destructive' : 'bg-subtle'}`} />
                {result.testType.toUpperCase()} {result.isAlive === true ? 'ALIVE' : result.isAlive === false ? 'DOWN' : 'UNKNOWN'}
                {detail != null && <span className={c.faint}>· {String(detail).slice(0, 42)}</span>}
              </span>
            )
          })}
        </div>
      </div>
      {showJson && (
        <pre className={`mt-3 max-h-52 overflow-y-auto rounded-md border p-3 text-[10px] leading-relaxed border-border bg-secondary text-muted-foreground`}>
          {JSON.stringify(run.results, null, 2)}
        </pre>
      )}
    </div>
  )
}

export function ProbeRunsPanel({
  curProbeRuns,
  theme,
}: {
  curProbeRuns: ProbeRun[]
  theme: ProcessedTheme
}) {
  const [showBetween, setShowBetween] = useState(false)
  const [showOlder,   setShowOlder]   = useState(false)
  const [openJsonId,  setOpenJsonId]  = useState<string | null>(null)
  const c = watchClasses(theme)

  const sorted = [...curProbeRuns].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  const latestProbeRun = sorted[0] ?? null
  const maxProbeOnCount = sorted.reduce((max, run) => Math.max(max, probeRunOnCount(run)), 0)
  const maxOnProbeRun = maxProbeOnCount > 0
    ? sorted
      .filter((run) => probeRunOnCount(run) === maxProbeOnCount)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null
    : null

  const maxOnTime = maxOnProbeRun ? new Date(maxOnProbeRun.createdAt).getTime() : -Infinity
  const between: ProbeRun[] = []
  const older: ProbeRun[] = []

  for (const run of sorted) {
    if (run.id === latestProbeRun?.id || run.id === maxOnProbeRun?.id) continue
    if (maxOnProbeRun && new Date(run.createdAt).getTime() > maxOnTime) {
      between.push(run)
    } else {
      older.push(run)
    }
  }

  const toggleJson = (id: string) => setOpenJsonId((prev) => (prev === id ? null : id))

  return (
    <Widget
      label={<>Probe runs · {curProbeRuns.length}</>}
      theme={theme}
      headerRight={<Clock className={`h-4 w-4 ${c.faint}`} />}
    >
      <div className="space-y-3">
        {latestProbeRun ? (
          <>
            <ProbeRunSnapshotCard
              title="Latest data"
              run={latestProbeRun}
              metric={`${probeRunOnCount(latestProbeRun)}/${latestProbeRun.results.length} on`}
              theme={theme}
              showJson={openJsonId === latestProbeRun.id}
              onToggleJson={() => toggleJson(latestProbeRun.id)}
            />

            {between.length > 0 && (
              <>
                <ExpandToggle count={between.length} open={showBetween} onToggle={() => setShowBetween((v) => !v)} label="between" c={c} />
                {showBetween && between.map((run) => (
                  <ProbeRunSnapshotCard
                    key={run.id}
                    title={compactDate(run.createdAt)}
                    run={run}
                    metric={`${probeRunOnCount(run)}/${run.results.length} on`}
                    theme={theme}
                    showJson={openJsonId === run.id}
                    onToggleJson={() => toggleJson(run.id)}
                  />
                ))}
              </>
            )}

            {maxOnProbeRun && maxOnProbeRun.id !== latestProbeRun.id && (
              <ProbeRunSnapshotCard
                title="Latest max on"
                run={maxOnProbeRun}
                metric={`${maxProbeOnCount}/${maxOnProbeRun.results.length} on`}
                theme={theme}
                showJson={openJsonId === maxOnProbeRun.id}
                onToggleJson={() => toggleJson(maxOnProbeRun.id)}
              />
            )}

            {older.length > 0 && (
              <>
                <ExpandToggle count={older.length} open={showOlder} onToggle={() => setShowOlder((v) => !v)} label="older" c={c} />
                {showOlder && older.map((run) => (
                  <ProbeRunSnapshotCard
                    key={run.id}
                    title={compactDate(run.createdAt)}
                    run={run}
                    metric={`${probeRunOnCount(run)}/${run.results.length} on`}
                    theme={theme}
                    showJson={openJsonId === run.id}
                    onToggleJson={() => toggleJson(run.id)}
                  />
                ))}
              </>
            )}
          </>
        ) : (
          <span className={`font-mono text-xs uppercase tracking-[0.28em] ${c.faint}`}>No probe data</span>
        )}
      </div>
    </Widget>
  )
}
